import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import ProductCard from "@/components/product/ProductCard";
import { productHref } from "@/lib/productUrl";
import useFeaturedProducts from "@/hooks/useFeaturedProducts";
import SectionHeader from "./SectionHeader";
import Reveal from "./Reveal";
import { HOME_WRAP } from "./homeTheme";

const DEFAULT_LEAD = "Precision-engineered tools and equipment for demanding industrial applications.";

/**
 * "Our Product Solutions" — the products chosen in Admin → Home Page → Product Solutions, in the order set there.
 *
 * The homepage stores only product references; each card is rendered from the PRODUCT RECORD (name, category, image, short
 * description, link) via the same catalogue `ProductCard` the listing pages use. So an image, title or description changed in
 * Admin → Products shows here — and on the listing, product page and related products — with no code change and nothing to
 * keep in sync. Hidden items are left out; a draft/deleted product is skipped. `label` (optional, per item) overrides only the
 * card title.
 */
export default function ProductShowcase({ data }) {
  const section = data?.productSolutionsSection || {};
  const { loading, entries } = useFeaturedProducts(data?.featuredProducts?.items);
  if (!loading && entries.length === 0) return null;
  return (
    <section id="products" className="bg-white py-24 lg:py-32" aria-labelledby="home-products-title">
      <div className={HOME_WRAP}>
        <SectionHeader
          eyebrow={section.title || "Our Product Solutions"}
          title={section.subtitle || "Advanced Tools. Reliable Results."} titleId="home-products-title"
          description={section.description || DEFAULT_LEAD}
          action={
            <Link to="/products" className="group inline-flex shrink-0 items-center gap-2 pb-1 text-sm font-semibold text-[#1769FF]">
              <span className="border-b border-transparent pb-0.5 transition-colors group-hover:border-[#1769FF]">View all products</span>
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
            </Link>
          }
          className="mb-12 lg:mb-16"
        />
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 lg:gap-6">
          {loading
            ? Array.from({ length: 4 }, (_, i) => <div key={i} className="min-h-[420px] animate-pulse rounded-2xl bg-[#F1F5F9]" aria-hidden="true" />)
            : entries.map(({ product, label }, i) => (
                <Reveal key={product.id} delay={(i % 4) * 0.07} y={15} className="h-full">
                  <ProductCard
                    variant="catalogue"
                    product={label ? { ...product, name: label } : product}
                    categoryLabel={product.category}
                    ctaLabel="Explore Product"
                    href={productHref(product)}
                  />
                </Reveal>
              ))}
        </div>
      </div>
    </section>
  );
}
